import { AuthDeviceViewMediaMode, AuthDeviceViewRequestStatus } from '@/enums/AuthDeviceActivity';
import type { AuthDeviceInspectResponse, AuthDeviceViewRequest } from '@/interfaces/AuthDeviceActivity';
import type { AuthOtpIssueStep, AuthOtpResponse } from '@/interfaces/AuthOtp';
import type {
    AuthChangedNumberRecoveryCompleteRequest,
    AuthChangedNumberRecoveryCompleteResponse,
    AuthChangedNumberRecoveryOtpRequest,
    AuthLoggedInPhoneChangeCompleteRequest,
    AuthLoggedInPhoneChangeCompleteResponse,
    AuthLoggedInPhoneChangeOtpRequest,
    AuthPhoneChangeOtpResponse,
} from '@/interfaces/AuthPhoneChange';
import { fetchWithCorrelationId, getResponseCorrelationId } from '@/lib/api/correlation';
import { extractApiErrorCode, extractApiErrorMessage } from '@/lib/api/errorPayload';
import { buildPathWithSearch } from '@/lib/url/searchParams';

import type { AuthPhonePolicy } from './authPhonePolicy';
import type {
    AuthAccountRecoveryCodesRequiredResponse,
    AuthVerifyAuthenticatedResponse,
    AuthVerifyResponse,
    DeviceApprovalMutateResponse,
    DeviceApprovalPollResponse,
    DeviceApprovalRefreshResponse,
    EndTrustedDeviceSessionResponse,
    PendingDeviceApprovalListResponse,
    RegistrationInviteListResponse,
    RecoveryCodeDeliveryIntent,
    RecoveryCodesEmailRequest,
    TrustedDeviceListResponse,
    WebBootstrapResponse,
    WebQrPairingPollResponse,
    WebQrPairingResponse,
    WebSessionResponse,
} from './types';

type JsonBody = Record<string, unknown>;

type CommonErrorPayload = {
    errorCode?: string;
    message?: string;
    popToastNotification?: {
        message?: string;
    };
    textFieldErrors?: Array<{
        errorText?: string;
    }>;
};

export class AuthApiError extends Error {
    public readonly code: string | null;
    public readonly correlationId: string | null;

    public constructor({ message, code, correlationId }: { message: string; code: string | null; correlationId: string | null }) {
        super(message);
        this.name = 'AuthApiError';
        this.code = code;
        this.correlationId = correlationId;
    }
}

const parseResponse = async <T>(response: Response) => {
    const payload = (await response.json().catch(() => null)) as T | CommonErrorPayload | null;
    const correlationId = getResponseCorrelationId(response);

    if (!response.ok) {
        throw new AuthApiError({
            message: extractApiErrorMessage(payload),
            code: extractApiErrorCode(payload),
            correlationId,
        });
    }

    return payload as T;
};

const requestJson = async <T>({
    path,
    method = 'GET',
    body,
    skipGlobalErrorToast = false,
}: {
    path: string;
    method?: 'GET' | 'POST';
    body?: JsonBody;
    skipGlobalErrorToast?: boolean;
}) => {
    const headers: Record<string, string> = {};

    if (body) {
        headers['Content-Type'] = 'application/json';
    }

    const { response } = await fetchWithCorrelationId(
        path,
        {
            method,
            credentials: 'include',
            headers,
            body: body ? JSON.stringify(body) : undefined,
            skipGlobalErrorToast,
        },
        {
            prefix: 'web',
        }
    );

    return parseResponse<T>(response);
};

export const sendOtp = (body: { phone: string; countryOptionId: string; issueStep: AuthOtpIssueStep }) =>
    requestJson<AuthOtpResponse>({
        path: '/api/web/auth/otp',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const getAuthPhonePolicy = () =>
    requestJson<{ authPhonePolicy: AuthPhonePolicy }>({
        path: '/api/web/auth/phone-policy',
        skipGlobalErrorToast: true,
    });

export const verifyOtp = (body: { phone: string; otpCode: string; countryOptionId: string; deviceId: string }) =>
    requestJson<AuthVerifyResponse>({
        path: '/api/web/auth/verify',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const verifyFirebasePhone = (body: { idToken: string; countryOptionId: string; deviceId: string }) =>
    requestJson<AuthVerifyResponse>({
        path: '/api/web/auth/firebase-phone/verify',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const verifyTrustedDeviceBootstrapEmail = (body: { registrationToken: string; emailOtpCode: string }) =>
    requestJson<AuthVerifyAuthenticatedResponse>({
        path: '/api/web/auth/registration/email/verify',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const sendRecoveryOtp = (body: { phone: string; countryOptionId: string; issueStep: AuthOtpIssueStep }) =>
    requestJson<AuthOtpResponse>({
        path: '/api/web/auth/recovery/otp',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const completeRecovery = (body: { phone: string; otpCode: string; countryOptionId: string; recoveryCode: string; deviceId: string }) =>
    requestJson<AuthAccountRecoveryCodesRequiredResponse>({
        path: '/api/web/auth/recovery/complete',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const completeFirebasePhoneRecovery = (body: { idToken: string; countryOptionId: string; recoveryCode: string; deviceId: string }) =>
    requestJson<AuthAccountRecoveryCodesRequiredResponse>({
        path: '/api/web/auth/recovery/firebase-phone/complete',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const acknowledgeRecoveryCodes = (body: { recoveryToken: string; deviceId: string; recoveryCodeDelivery?: RecoveryCodeDeliveryIntent }) =>
    requestJson<AuthVerifyAuthenticatedResponse>({
        path: '/api/web/auth/recovery/codes/ack',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const sendRecoveryCodesEmail = (body: RecoveryCodesEmailRequest) =>
    requestJson<{ ok: boolean }>({
        path: '/api/web/auth/recovery/codes/email',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const requestLoggedInPhoneChangeOtp = (body: AuthLoggedInPhoneChangeOtpRequest) =>
    requestJson<AuthPhoneChangeOtpResponse>({
        path: '/api/web/auth/phone-change/otp',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const completeLoggedInPhoneChange = (body: AuthLoggedInPhoneChangeCompleteRequest) =>
    requestJson<AuthLoggedInPhoneChangeCompleteResponse>({
        path: '/api/web/auth/phone-change/complete',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const requestChangedNumberRecoveryOtp = (body: AuthChangedNumberRecoveryOtpRequest) =>
    requestJson<AuthPhoneChangeOtpResponse>({
        path: '/api/web/auth/recovery/changed-number/otp',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const completeChangedNumberRecovery = (body: AuthChangedNumberRecoveryCompleteRequest) =>
    requestJson<AuthChangedNumberRecoveryCompleteResponse>({
        path: '/api/web/auth/recovery/changed-number/complete',
        method: 'POST',
        body,
        skipGlobalErrorToast: true,
    });

export const logoutWeb = () =>
    requestJson<Record<string, never>>({
        path: '/api/web/auth/logout',
        method: 'POST',
        body: {},
    });

export const getWebSession = () =>
    requestJson<WebSessionResponse>({
        path: '/api/web/session',
    });

export const getWebBootstrap = () =>
    requestJson<WebBootstrapResponse>({
        path: '/api/web/bootstrap',
    });

export const createWebQrPairing = (body: { deviceId: string }) =>
    requestJson<WebQrPairingResponse>({
        path: '/api/web/auth/qr/create',
        method: 'POST',
        body,
    });

export const pollWebQrPairing = ({ token, deviceId }: { token: string; deviceId: string }) =>
    requestJson<WebQrPairingPollResponse>({
        path: buildPathWithSearch({
            pathname: '/api/web/auth/qr/poll',
            values: {
                token,
                deviceId,
            },
        }),
    });

export const pollDeviceApproval = ({ token, deviceId }: { token: string; deviceId: string }) =>
    requestJson<DeviceApprovalPollResponse>({
        path: buildPathWithSearch({
            pathname: '/api/web/auth/device-approval/poll',
            values: {
                token,
                deviceId,
            },
        }),
    });

export const refreshDeviceApproval = ({ token, deviceId }: { token: string; deviceId: string }) =>
    requestJson<DeviceApprovalRefreshResponse>({
        path: '/api/web/auth/device-approval/refresh',
        method: 'POST',
        body: {
            token,
            deviceId,
        },
    });

export const listPendingDeviceApprovals = () =>
    requestJson<PendingDeviceApprovalListResponse>({
        path: '/api/web/auth/device-approval/pending',
    });

export const approveDeviceApproval = ({ token }: { token: string }) =>
    requestJson<DeviceApprovalMutateResponse>({
        path: '/api/web/auth/device-approval/approve',
        method: 'POST',
        body: {
            token,
        },
    });

export const rejectDeviceApproval = ({ token, reason }: { token: string; reason?: string }) =>
    requestJson<DeviceApprovalMutateResponse>({
        path: '/api/web/auth/device-approval/reject',
        method: 'POST',
        body: {
            token,
            reason,
        },
    });

export const listTrustedDevices = () =>
    requestJson<TrustedDeviceListResponse>({
        path: '/api/web/auth/devices',
    });

export const revokeTrustedDevice = ({ deviceId }: { deviceId: string }) =>
    requestJson<{ status: string }>({
        path: '/api/web/auth/devices/revoke',
        method: 'POST',
        body: {
            deviceId,
        },
    });

export const endTrustedDeviceSession = ({ deviceId }: { deviceId: string }) =>
    requestJson<EndTrustedDeviceSessionResponse>({
        path: '/api/web/auth/devices/end-session',
        method: 'POST',
        body: {
            deviceId,
        },
    });

export const inspectTrustedDevice = ({ deviceId }: { deviceId: string }) =>
    requestJson<AuthDeviceInspectResponse>({
        path: `/api/web/auth/devices/${deviceId}/inspect`,
    });

export const requestTrustedDeviceView = ({ deviceId, mode }: { deviceId: string; mode: AuthDeviceViewMediaMode }) =>
    requestJson<{ request: AuthDeviceViewRequest }>({
        path: `/api/web/auth/devices/${deviceId}/view/request`,
        method: 'POST',
        body: {
            mode,
        },
    });

export const respondTrustedDeviceView = ({
    requestId,
    status,
}: {
    requestId: string;
    status: AuthDeviceViewRequestStatus.Accepted | AuthDeviceViewRequestStatus.Declined;
}) =>
    requestJson<{ request: AuthDeviceViewRequest }>({
        path: `/api/web/auth/devices/view/${requestId}/respond`,
        method: 'POST',
        body: {
            status,
        },
    });

export const updateTrustedDeviceViewStatus = ({
    requestId,
    status,
}: {
    requestId: string;
    status:
        | AuthDeviceViewRequestStatus.Failed
        | AuthDeviceViewRequestStatus.PermissionRequired
        | AuthDeviceViewRequestStatus.Starting
        | AuthDeviceViewRequestStatus.Stopped
        | AuthDeviceViewRequestStatus.Streaming;
}) =>
    requestJson<{ request: AuthDeviceViewRequest }>({
        path: `/api/web/auth/devices/view/${requestId}/status`,
        method: 'POST',
        body: {
            status,
        },
    });

export const listRegistrationInvites = () =>
    requestJson<RegistrationInviteListResponse>({
        path: '/api/web/auth/invites',
    });

export const revokeRegistrationInvite = ({ token }: { token: string }) =>
    requestJson<{ status: string }>({
        path: '/api/web/auth/invites/revoke',
        method: 'POST',
        body: {
            token,
        },
    });
